import { useLayoutEffect, useRef, useState, type RefObject } from "react";
import type { SessionDetail } from "../../../../shared/api.ts";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { TabCount, Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { cn } from "@/lib/utils.ts";
import { ClassificationTab } from "./classification-tab.tsx";
import { collectFiles, FilesTab } from "./files-tab.tsx";
import { collectImages, ImagesTab } from "./images-tab.tsx";
import { SourcesTab } from "./sources-tab.tsx";

export type PanelTab = "classification" | "sources" | "images" | "files";

/** Denser than the default tabs, so four labels with counts fit the 400px panel in Ukrainian. */
const TRIGGER = "gap-1 px-1.5 text-[12.5px]";

/** Which ends of a horizontal scroller hide content, so the tab strip can fade there. */
function useHiddenEdges(ref: RefObject<HTMLElement | null>): { start: boolean; end: boolean } {
  const [edges, setEdges] = useState({ start: false, end: false });
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const update = () => {
      const start = node.scrollLeft > 1;
      const end = node.scrollLeft + node.clientWidth < node.scrollWidth - 1;
      setEdges((current) => (current.start === start && current.end === end ? current : { start, end }));
    };
    update();
    node.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(node);
    if (node.firstElementChild) observer.observe(node.firstElementChild);
    return () => {
      node.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [ref]);
  return edges;
}

/**
 * The right panel: Classification, Sources (count), Images (count), Files (count).
 * The tab strip scrolls sideways when the labels do not fit, and keeps the active tab in view.
 */
export function RightPanel({
  detail,
  readOnly,
  tab,
  onTabChange,
  tabs = ["classification", "sources", "images", "files"],
  className,
}: {
  detail: SessionDetail;
  readOnly: boolean;
  tab: PanelTab;
  onTabChange: (tab: PanelTab) => void;
  tabs?: PanelTab[];
  className?: string;
}) {
  const { t } = useI18n();
  const strip = useRef<HTMLDivElement>(null);
  const focusActive = useRef(false);
  const edges = useHiddenEdges(strip);
  const files = collectFiles(detail);
  const fileCount = files.uploads.length + files.stored.length;
  const imageCount = collectImages(detail).length;
  const active = tabs.includes(tab) ? tab : (tabs[0] ?? "classification");

  useLayoutEffect(() => {
    const node = strip.current;
    const trigger = node?.querySelector<HTMLElement>('[data-slot="tabs-trigger"][data-state="active"]');
    if (!node || !trigger) return;
    const box = node.getBoundingClientRect();
    const rect = trigger.getBoundingClientRect();
    if (rect.left < box.left) node.scrollLeft -= box.left - rect.left + 16;
    else if (rect.right > box.right) node.scrollLeft += rect.right - box.right + 16;
    // A link in a tab moved the panel: focus follows it, instead of dropping to the page.
    if (focusActive.current) {
      focusActive.current = false;
      trigger.focus();
    }
  }, [active]);

  const showTab = (next: PanelTab) => {
    focusActive.current = true;
    onTabChange(next);
  };

  return (
    <Tabs value={active} onValueChange={(value) => onTabChange(value as PanelTab)} className={cn("flex min-h-0 flex-1 flex-col", className)}>
      <div className="relative shrink-0">
        <div ref={strip} className="overflow-x-auto overflow-y-hidden overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <TabsList className="w-max min-w-full gap-0.5 bg-panel px-2">
            {tabs.includes("classification") ? (
              <TabsTrigger value="classification" className={TRIGGER}>
                {t.panel.tabs.classification}
              </TabsTrigger>
            ) : null}
            {tabs.includes("sources") ? (
              <TabsTrigger value="sources" className={TRIGGER}>
                {t.panel.tabs.sources}
                <TabCount value={detail.sources.length} />
              </TabsTrigger>
            ) : null}
            {tabs.includes("images") ? (
              <TabsTrigger value="images" className={TRIGGER}>
                {t.panel.tabs.images}
                <TabCount value={imageCount} />
              </TabsTrigger>
            ) : null}
            {tabs.includes("files") ? (
              <TabsTrigger value="files" className={TRIGGER}>
                {t.panel.tabs.files}
                <TabCount value={fileCount} />
              </TabsTrigger>
            ) : null}
          </TabsList>
        </div>
        {edges.start ? <span aria-hidden className="pointer-events-none absolute top-0 bottom-px left-0 w-6 bg-gradient-to-r from-panel to-panel/0" /> : null}
        {edges.end ? <span aria-hidden className="pointer-events-none absolute top-0 right-0 bottom-px w-10 bg-gradient-to-l from-panel via-panel/80 to-panel/0" /> : null}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <TabsContent value="classification">
          <ClassificationTab detail={detail} readOnly={readOnly} onShowImages={() => showTab("images")} />
        </TabsContent>
        <TabsContent value="sources">
          <SourcesTab sources={detail.sources} turns={detail.turns} />
        </TabsContent>
        <TabsContent value="images">
          <ImagesTab detail={detail} />
        </TabsContent>
        <TabsContent value="files">
          <FilesTab detail={detail} />
        </TabsContent>
      </div>
    </Tabs>
  );
}

export function RightPanelSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-4" aria-hidden>
      <div className="flex gap-3 border-b pb-3">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-5 w-16" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Skeleton className="aspect-square" />
        <Skeleton className="aspect-square" />
        <Skeleton className="aspect-square" />
      </div>
      <Skeleton className="h-5 w-3/4" />
      <Skeleton className="h-3.5 w-full" />
      <Skeleton className="h-3.5 w-5/6" />
      <Skeleton className="mt-2 h-40 w-full rounded-xl" />
    </div>
  );
}
